import { getDB } from '../db/direct-couchdb';
import subscriptionService from './subscription-service';
import productService from '../db/product-service';

class SubscriptionOrderGenerator {
  // Get current DB instance with auth headers
  get db() {
    return getDB();
  }

  // Generate orders for a specific date
  async generateOrdersForDate(targetDate) {
    try {
      const activeSubscriptions = await this.getActiveSubscriptions();
      const orders = [];

      for (const subscription of activeSubscriptions) {
        const shouldGenerate = await this.shouldGenerateOrder(subscription, targetDate);
        if (shouldGenerate) {
          const order = await this.createSubscriptionOrder(subscription, targetDate);
          if (order) orders.push(order);
        }
      }

      return orders;
    } catch (error) {
      console.error('Error generating subscription orders:', error);
      return [];
    }
  }

  async getActiveSubscriptions() {
    const result = await this.db.find({
      selector: { 
        type: 'user_subscription',
        status: 'active'
      }
    });
    return result.docs;
  }

  async shouldGenerateOrder(subscription, targetDate) {
    const date = new Date(targetDate);
    const dayOfWeek = date.getDay();
    
    // Only Monday-Friday (1-5)
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
    
    // Check if this date is in the subscription's delivery schedule
    const dateKey = date.toISOString().split('T')[0];
    console.log('DEBUG: Checking subscription', subscription._id, 'for date:', dateKey);
    console.log('DEBUG: Delivery schedule keys:', Object.keys(subscription.deliverySchedule || {}));
    if (!subscription.deliverySchedule || !subscription.deliverySchedule[dateKey]) {
      console.log('DEBUG: Date not in delivery schedule:', dateKey);
      return false;
    }
    
    console.log('DEBUG: Date IS in schedule, checking if order exists');
    // Check if order already exists for this date
    const exists = await this.orderExistsForDate(subscription._id, targetDate);
    console.log('DEBUG: Order exists?', exists);
    return !exists;
  }

  async orderExistsForDate(subscriptionId, date) {
    try {
      const dateKey = date.toISOString().split('T')[0];
      await this.db.get(`sub_order_${subscriptionId}_${dateKey}`);
      return true;
    } catch (error) {
      if (error.status === 404) return false;
      return false;
    }
  }

  async createSubscriptionOrder(subscription, targetDate) {
    try {
      const dateKey = targetDate.toISOString().split('T')[0];
      const daySchedule = subscription.deliverySchedule[dateKey];
      
      if (!daySchedule) {
        console.log('DEBUG: No schedule for date:', dateKey);
        return null;
      }
      
      const products = await productService.getAllProducts();
      const orderItems = [];
      let totalAmount = 0;

      // Build order items from the day's schedule
      if (daySchedule.shot) {
        const product = products.find(p => p._id === daySchedule.shot);
        if (product) {
          orderItems.push({
            productId: product._id,
            productName: product.name,
            quantity: 1,
            price: product.price,
            total: product.price
          });
          totalAmount += product.price;
        }
      }
      
      if (daySchedule.smoothie) {
        const product = products.find(p => p._id === daySchedule.smoothie);
        if (product) {
          orderItems.push({
            productId: product._id,
            productName: product.name,
            quantity: 1,
            price: product.price,
            total: product.price
          });
          totalAmount += product.price;
        }
      }
      
      if (orderItems.length === 0) {
        console.log('DEBUG: No items for date:', dateKey);
        return null;
      }

      const order = {
        _id: `sub_order_${subscription._id}_${dateKey}`,
        type: 'subscription_order',
        subscriptionId: subscription._id,
        customerId: subscription.customerId,
        planType: subscription.planType,
        orderItems,
        totalAmount,
        deliveryDate: targetDate.toISOString().split('T')[0],
        status: 'confirmed',
        isSubscriptionOrder: true,
        createdAt: new Date().toISOString()
      };

      await this.db.put(order);
      
      // Update subscription delivery count
      await this.updateDeliveryCount(subscription);
      
      return order;
    } catch (error) {
      console.error('Error creating subscription order:', error);
      return null;
    }
  }

  async updateDeliveryCount(subscription) {
    try {
      subscription.deliveriesCompleted = (subscription.deliveriesCompleted || 0) + 1;
      subscription.deliveriesRemaining = Math.max(0, 20 - subscription.deliveriesCompleted);
      await this.db.put(subscription);
    } catch (error) {
      console.error('Error updating delivery count:', error);
    }
  }

  // Generate orders for next 7 days
  async generateWeeklyOrders() {
    const orders = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dailyOrders = await this.generateOrdersForDate(date);
      orders.push(...dailyOrders);
    }
    return orders;
  }

  // Get subscription orders for delivery dashboard
  async getSubscriptionOrdersForDate(date) {
    try {
      const result = await this.db.find({
        selector: {
          type: 'subscription_order',
          deliveryDate: date
        }
      });
      return result.docs;
    } catch (error) {
      console.error('Error fetching subscription orders:', error);
      return [];
    }
  }
}

export default new SubscriptionOrderGenerator();